import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  Dataset as DatasetIcon,
  Functions as FunctionsIcon,
  Key as KeyIcon,
  History as HistoryIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Code as CodeIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';

interface CTATourProps {
  open: boolean;
  onClose: () => void;
  onNavigate?: (path: string) => void;
}

const tourSteps = [
  'One-Click Functions',
  'Custom Objects',
  'Function Creation',
  'API Keys & Integration',
  'Run History',
];

export const CTATour: React.FC<CTATourProps> = ({ open, onClose, onNavigate }) => {
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, tourSteps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleLinkClick = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
      onClose();
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <AutoAwesomeIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
              <Typography variant="h5">One-Click AI Functions</Typography>
            </Box>
            
            <Typography variant="body1" paragraph>
              Get started instantly with pre-built AI functions! No coding required.
            </Typography>

            <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                How it works:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Browse our library of ready-to-use functions" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Click 'Add to My Functions' to instantly deploy" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Optionally customize with AI assistance" />
                </ListItem>
              </List>
            </Paper>

            <Typography variant="body2" color="text.secondary" paragraph>
              Perfect for common tasks like sentiment analysis, data extraction, summarization, and more!
            </Typography>

            <Button
              variant="outlined"
              startIcon={<ArrowForwardIcon />}
              onClick={() => handleLinkClick('/dashboard')}
            >
              Try One-Click Functions
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <DatasetIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
              <Typography variant="h5">Custom Objects</Typography>
            </Box>
            
            <Typography variant="body1" paragraph>
              Define structured data formats for your AI functions to return consistent, typed responses.
            </Typography>

            <Paper sx={{ p: 2, mb: 3, bgcolor: 'secondary.50' }}>
              <Typography variant="subtitle2" gutterBottom color="secondary.main">
                Benefits:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Type-safe responses with validation" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Reusable across multiple functions" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Auto-generated SDKs with proper types" />
                </ListItem>
              </List>
            </Paper>

            <Typography variant="body2" color="text.secondary" paragraph>
              Create objects for products, users, analytics, or any custom data structure your application needs.
            </Typography>

            <Button
              variant="outlined"
              startIcon={<ArrowForwardIcon />}
              onClick={() => handleLinkClick('/dashboard/objects')}
            >
              Manage Custom Objects
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <FunctionsIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
              <Typography variant="h5">Create AI Functions</Typography>
            </Box>
            
            <Typography variant="body1" paragraph>
              Build powerful custom AI functions tailored to your specific needs.
            </Typography>

            <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.50' }}>
              <Typography variant="subtitle2" gutterBottom color="info.main">
                Features:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CodeIcon color="info" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Multiple input types: text, JSON, images, PDFs, and more" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><AutoAwesomeIcon color="info" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="AI assistance for writing effective prompts" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Version control with automatic improvements" />
                </ListItem>
              </List>
            </Paper>

            <Typography variant="body2" color="text.secondary" paragraph>
              Use the AI improver to enhance your functions over time based on real-world usage.
            </Typography>

            <Button
              variant="outlined"
              startIcon={<ArrowForwardIcon />}
              onClick={() => handleLinkClick('/dashboard/ai-functions')}
            >
              Create New Function
            </Button>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <KeyIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
              <Typography variant="h5">API Keys & Integration</Typography>
            </Box>
            
            <Typography variant="body1" paragraph>
              Integrate your AI functions into any application with our simple REST API.
            </Typography>

            <Paper sx={{ p: 2, mb: 3, bgcolor: 'warning.50' }}>
              <Typography variant="subtitle2" gutterBottom color="warning.main">
                Integration Tools:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><KeyIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Secure API keys with granular permissions" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CodeIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Code examples in multiple languages" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="OpenAPI/Swagger documentation" />
                </ListItem>
              </List>
            </Paper>

            <Typography variant="body2" color="text.secondary" paragraph>
              Each function includes ready-to-use code snippets. Just copy, paste, and run!
            </Typography>

            <Button
              variant="outlined"
              startIcon={<ArrowForwardIcon />}
              onClick={() => handleLinkClick('/dashboard/api-keys')}
            >
              Manage API Keys
            </Button>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <HistoryIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
              <Typography variant="h5">Run History & Analytics</Typography>
            </Box>
            
            <Typography variant="body1" paragraph>
              Monitor usage, debug issues, and optimize your AI functions with detailed analytics.
            </Typography>

            <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.50' }}>
              <Typography variant="subtitle2" gutterBottom color="success.main">
                Analytics Features:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><HistoryIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Detailed execution logs with inputs/outputs" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><PlayArrowIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Performance metrics and token usage" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Per-API-key usage tracking" />
                </ListItem>
              </List>
            </Paper>

            <Typography variant="body2" color="text.secondary" paragraph>
              Use insights from your run history to improve prompts and reduce costs.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowForwardIcon />}
                onClick={() => handleLinkClick('/dashboard/analytics')}
              >
                View Analytics
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowForwardIcon />}
                onClick={() => handleLinkClick('/dashboard/runs')}
              >
                View Run History
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Welcome to Your AI Platform</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {tourSteps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ minHeight: 400 }}>
          {renderStepContent()}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>Skip Tour</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
        >
          Back
        </Button>
        {activeStep < tourSteps.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={onClose} color="success">
            Get Started
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
